// frontend/src/components/Modal/CreateEventSelectVehicleModal.js
import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import styled from 'styled-components';
import Button from '../Button/Button';
import Typography from '../Typography';
import { useTranslation } from 'react-i18next';
import axiosClient from '../../api/axiosClient';
import { useAuth } from '../../context/AuthContext';
import VehicleListItem from '../VehicleListItem/VehicleListItem';
import ChevronDownIcon from '../Icons/ChevronDownIcon';
import { theme } from '../../theme';
import AddVehicleToGarageModal from './AddVehicleToGarageModal';
import { VehicleProvider } from '../../context/VehicleContext';
import AddCircleOutlineIcon from '../Icons/AddCircleOutlineIcon';
import { getVehicleTypesWithLabels } from '../../constants/vehicleTypes';
import WheelIcon from '../Icons/WheelIcon';

const CreateEventSelectOrganizerVehicleModal = ({ onSave, selectedVehicle, onClose, allowedVehicleTypes = [] }) => {
  const { t } = useTranslation(['createEvent', 'vehicleTypes']);
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInvalidVehicles, setShowInvalidVehicles] = useState(false);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [selectedVehicleLocal, setSelectedVehicleLocal] = useState(selectedVehicle || null);

  useEffect(() => {
    const fetchVehicles = async () => {
      setLoading(true);
      try {
        const response = await axiosClient.get(`/api/vehicles/user/${user.id}`);
        setVehicles(response.data.vehicles);
      } catch (error) {
        console.error('Error fetching vehicles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, [user.id]);

  const vehicleTypesWithLabels = getVehicleTypesWithLabels();
  const allVehicleTypeValues = vehicleTypesWithLabels.map((v) => v.value);
  const vehicleTypeLabels = vehicleTypesWithLabels.reduce((acc, cur) => {
    acc[cur.value] = cur.label;
    return acc;
  }, {});
  const allowedVehicleTypesNormalized = Array.isArray(allowedVehicleTypes)
    ? allowedVehicleTypes.filter((type) => allVehicleTypeValues.includes(type))
    : [];
  const allowedVehicleTypesEffective = allowedVehicleTypesNormalized.length > 0 ? allowedVehicleTypesNormalized : allVehicleTypeValues;
  const allowedVehicleTypesLabel = allowedVehicleTypesEffective
    .map((type) => vehicleTypeLabels[type] || type)
    .join(', ')
    .toLowerCase();

  useEffect(() => {
    const selectedVehicleType = selectedVehicleLocal?.vehicleType || 'motorcycle';
    if (selectedVehicleLocal && !allowedVehicleTypesEffective.includes(selectedVehicleType)) {
      setSelectedVehicleLocal(null);
    }
  }, [allowedVehicleTypesEffective, selectedVehicleLocal]);

  // Separar vehículos válidos e inválidos
  const validVehicles = vehicles.filter(vehicle => {
    const vehicleType = vehicle.vehicleType || 'motorcycle';
    return allowedVehicleTypesEffective.includes(vehicleType);
  });

  const invalidVehicles = vehicles.filter(vehicle => {
    const vehicleType = vehicle.vehicleType || 'motorcycle';
    return !allowedVehicleTypesEffective.includes(vehicleType);
  });

  const handleVehicleAdded = (newVehicle) => {
    setVehicles(prev => {
      const exists = prev.some(v => v._id === newVehicle._id);
      const updated = exists ? prev.map(v => (v._id === newVehicle._id ? newVehicle : v)) : [...prev, newVehicle];
      return updated;
    });

    const newType = newVehicle.vehicleType || 'motorcycle';
    const isValid = !allowedVehicleTypes || allowedVehicleTypes.length === 0 || allowedVehicleTypes.includes(newType);
    if (isValid) {
      setSelectedVehicleLocal(newVehicle);
    } else {
      setShowInvalidVehicles(true);
    }
  };

  const handleVehicleSelect = (vehicle) => {
    // Solo permitir selección de vehículos válidos
    const vehicleType = vehicle.vehicleType || 'motorcycle';
    if (allowedVehicleTypesEffective.includes(vehicleType)) {
      setSelectedVehicleLocal((prev) => (prev?._id === vehicle._id ? null : vehicle));
    }
  };

  return (
    <Modal onClose={onClose} title={t('selectVehicleModal.title')} closeOnOverlayClick={false}>
      <ModalContent>
        <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultWeak}>
          {t('selectVehicleModal.subtitle')}
        </Typography>
        {loading ? (
          <Typography as="p" $variant="body-2-medium">
            {t('selectVehicleModal.loading')}
          </Typography>
        ) : vehicles.length > 0 ? (
          <>
            {validVehicles.length > 0 ? (
              <VehicleSection>
                <Typography as="h3" $variant="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
                  {t('selectVehicleModal.compatibleVehiclesTitle', { types: allowedVehicleTypesLabel })}
                </Typography>
                <VehicleList>
                  {validVehicles.map((vehicle) => (
                    <VehicleListItem
                      key={vehicle._id}
                      vehicle={vehicle}
                      selected={vehicle._id === selectedVehicleLocal?._id}
                      onClick={() => handleVehicleSelect(vehicle)}
                    />
                  ))}
                  <Button size="l" $variant="outline" onClick={() => setShowAddVehicleModal(true)}>
                    <AddCircleOutlineIcon fill={theme.colors.defaultMain} width={20} height={20} />
                    {t('selectVehicleModal.addVehicle')}
                  </Button>
                </VehicleList>
              </VehicleSection>
            ) : (
              //Empty state: No compatible vehicles
              <VehicleSection>
                <NoVehicleMsg>
                  <NoVehicleMsgWrapper>
                    <WheelIcon fill={theme.colors.defaultWeak} width="20" height="20" />
                    <Typography as="p" $variant="body-1-semibold" $align="center" color={theme.colors.defaultWeak}>
                      {t('selectVehicleModal.noCompatibleVehiclesTitle', { types: allowedVehicleTypesLabel })}
                    </Typography>
                  </NoVehicleMsgWrapper>
                  <Button size="l" $variant="outline" onClick={() => setShowAddVehicleModal(true)}>
                    <AddCircleOutlineIcon fill={theme.colors.defaultMain} width={20} height={20} />
                    {t('selectVehicleModal.addVehicle')}
                  </Button>
                </NoVehicleMsg>
              </VehicleSection>
            )}

            {invalidVehicles.length > 0 && (
              <VehicleSection>
                <InvalidVehiclesHeader onClick={() => setShowInvalidVehicles(!showInvalidVehicles)}>
                  <Typography as="h3" $variant="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultWeak}>
                    {t('selectVehicleModal.invalidVehicles')}
                  </Typography>
                  <ChevronIconWrapper $isOpen={showInvalidVehicles}>
                    <ChevronDownIcon fill={theme.colors.defaultWeak} width={20} height={20} />
                  </ChevronIconWrapper>
                </InvalidVehiclesHeader>
                {showInvalidVehicles && (
                  <VehicleList>
                    {invalidVehicles.map((vehicle) => (
                      <VehicleListItem
                        key={vehicle._id}
                        vehicle={vehicle}
                        selected={false}
                        disabled={true}
                        onClick={() => { }} // No hacer nada al hacer click
                      />
                    ))}
                  </VehicleList>
                )}

              </VehicleSection>
            )}
          </>
        ) : (
          //Empty state: No vehicles
          <VehicleSection>
            <NoVehicleMsg>
              <NoVehicleMsgWrapper>
                <WheelIcon fill={theme.colors.defaultWeak} width="20" height="20" />
                <Typography as="p" $variant="body-1-semibold" $align="center" color={theme.colors.defaultWeak}>
                  {t('selectVehicleModal.noVehiclesTitle')}
                </Typography>
                <Typography as="p" $variant="body-2-medium" $align="center" color={theme.colors.defaultWeak}>
                  {t('selectVehicleModal.noVehiclesSubtitle')}
                </Typography>
              </NoVehicleMsgWrapper>
              <Button size="l" $variant="outline" onClick={() => setShowAddVehicleModal(true)}>
                <AddCircleOutlineIcon fill={theme.colors.defaultMain} width={20} height={20} />
                {t('selectVehicleModal.addVehicle')}
              </Button>
            </NoVehicleMsg>
          </VehicleSection>
        )}
      </ModalContent>
      <FormActions>
        <Button
          size="m"
          $fullWidth
          onClick={() => {
            onSave?.(selectedVehicleLocal);
            onClose?.();
          }}
        >
          {t('selectVehicleModal.save')}
        </Button>
      </FormActions>
      <VehicleProvider>
        <AddVehicleToGarageModal
          isOpen={showAddVehicleModal}
          onClose={() => setShowAddVehicleModal(false)}
          onVehicleAdded={handleVehicleAdded}
        />
      </VehicleProvider>
    </Modal>
  );
};

export default CreateEventSelectOrganizerVehicleModal;

const ModalContent = styled.div`
  background-color: ${({ theme }) => theme.fill.defaultMain};
  padding: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const VehicleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const VehicleSection = styled.div`
  padding-top: ${({ theme }) => theme.sizing.md};  
  padding-bottom: ${({ theme }) => theme.sizing.md};
  gap: ${({ theme }) => theme.sizing.xs};
  display: flex;
  flex-direction: column;
`;

const InvalidVehiclesHeader = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 0px;
  // margin-bottom: ${({ theme }) => theme.sizing.sm};
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const ChevronIconWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  transition: transform 0.3s ease;
  transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const FormActions = styled.div`
  border-top: 1px solid ${({ theme }) => theme.border.defaultWeak};
  padding: ${({ theme }) => theme.sizing.sm};
  text-align: center;
  width: 100%;
`;

const NoVehicleMsg = styled.div`
  display: flex;
  flex-direction: column;
  justify-items: flex-start;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.md};
  padding-bottom: ${({ theme }) => theme.sizing.sm};
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding-left: ${({ theme }) => theme.sizing.xs};
    padding-right: ${({ theme }) => theme.sizing.xs};
  }
`;

const NoVehicleMsgWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-items: flex-start;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
`;
